import { useContext, useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  TextField,
  Box,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { LanguageContext } from "App";
import { Add, Delete } from "@mui/icons-material";
import DatePicker from "react-datepicker";
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  isSameDay,
  isBefore,
  isAfter,
  parseISO,
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

const QuickDateRangePicker = ({
  range,
  index,
  onRangeChange,
  onRemove,
  showRemove,
  future = false,
  disabledDates = [],
  individualDatesMode = false,
}) => {
  const { translations } = useContext(LanguageContext);
  const [quickDate, setQuickDate] = useState("All Time");
  const [startDate, setStartDate] = useState(
    range.startDate ? new Date(range.startDate) : null
  );
  const [endDate, setEndDate] = useState(
    range.endDate ? new Date(range.endDate) : null
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState(range.selectedDates || []);

  const futureDates = [
    { value: "Today", label: "Today" },
    { value: "Yesterday", label: "Yesterday" },
    { value: "Tomorrow", label: "Tomorrow" },
    { value: "This Week", label: "This Week" },
    { value: "Next Week", label: "Next Week" },
    { value: "This Month", label: "This Month" },
    { value: "Next Month", label: "Next Month" },
    { value: "This Year", label: "This Year" },
  ];

  const normalDates = [
    { value: "Today", label: "Today" },
    { value: "Tomorrow", label: "Tomorrow" },
    { value: "This Week", label: "This Week" },
    { value: "Last Week", label: "Last Week" },
    { value: "This Month", label: "This Month" },
    { value: "Last Month", label: "Last Month" },
    { value: "This Year", label: "This Year" },
  ];

  const allTime = { value: "All Time", label: "All Time" };
  const custom = { value: "Custom", label: "Custom" };

  const isDateDisabled = (date) => {
    return disabledDates.some((disabledRange) => {
      const disabledStart = parseISO(disabledRange.startDate);
      const disabledEnd = parseISO(disabledRange.endDate);
      return (
        isSameDay(date, disabledStart) ||
        isSameDay(date, disabledEnd) ||
        (isAfter(date, disabledStart) && isBefore(date, disabledEnd))
      );
    });
  };

  useEffect(() => {
    if (
      range &&
      !range.startDate &&
      !range.endDate &&
      (!range.selectedDates || range.selectedDates.length === 0)
    ) {
      setStartDate(null);
      setEndDate(null);
      setSelectedDates([]);
      setQuickDate("All Time");
    }
  }, [range]);

  useEffect(() => {
    if (!startDate && !endDate && selectedDates.length === 0) {
      setQuickDate("All Time");
    }
  }, [startDate, endDate, selectedDates]);

  const handleQuickDateChange = (value) => {
    setQuickDate(value);

    let newStartDate = null;
    let newEndDate = null;
    let newSelectedDates = [];

    const now = new Date();

    switch (value) {
      case "Today":
        newStartDate = now;
        newEndDate = now;
        setQuickDate("Today");
        break;
      case "Yesterday":
        newStartDate = addDays(now, -1);
        newEndDate = addDays(now, -1);
        setQuickDate("Yesterday");
        break;
      case "Tomorrow":
        newStartDate = addDays(now, 1);
        newEndDate = addDays(now, 1);
        setQuickDate("Tomorrow");
        break;
      case "This Week":
        newStartDate = startOfWeek(now);
        newEndDate = endOfWeek(now);
        setQuickDate("This Week");
        break;
      case "Next Week":
        newStartDate = addDays(startOfWeek(now), 7);
        newEndDate = addDays(endOfWeek(now), 7);
        setQuickDate("Next Week");
        break;
      case "Last Week":
        newStartDate = subWeeks(startOfWeek(now), 1);
        newEndDate = subWeeks(endOfWeek(now), 1);
        setQuickDate("Last Week");
        break;
      case "This Month":
        newStartDate = startOfMonth(now);
        newEndDate = endOfMonth(now);
        setQuickDate("This Month");
        break;
      case "Next Month":
        newStartDate = startOfMonth(addDays(endOfMonth(now), 1));
        newEndDate = endOfMonth(addDays(endOfMonth(now), 1));
        setQuickDate("Next Month");
        break;
      case "Last Month":
        newStartDate = subMonths(startOfMonth(now), 1);
        newEndDate = subMonths(endOfMonth(now), 1);
        setQuickDate("Last Month");
        break;
      case "This Year":
        newStartDate = startOfYear(now);
        newEndDate = endOfYear(now);
        setQuickDate("This Year");
        break;
      case "All Time":
        newStartDate = null;
        newEndDate = null;
        break;
      case "Custom":
        setIsCalendarOpen(true);
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedDates(newSelectedDates);

    onRangeChange(index, {
      startDate: newStartDate ? format(newStartDate, "yyyy-MM-dd") : "",
      endDate: newEndDate ? format(newEndDate, "yyyy-MM-dd") : "",
      selectedDates: newSelectedDates,
    });
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;

    let validStart = start;
    let validEnd = end;

    if (start && isDateDisabled(start)) {
      validStart = null;
    }

    if (end && isDateDisabled(end)) {
      validEnd = null;
    }

    setStartDate(validStart);
    setEndDate(validEnd);

    if (validStart || validEnd) {
      setQuickDate("Custom");
    }

    onRangeChange(index, {
      startDate: validStart ? format(validStart, "yyyy-MM-dd") : "",
      endDate: validEnd ? format(validEnd, "yyyy-MM-dd") : "",
      selectedDates: [],
    });
  };

  const handleIndividualDateChange = (date) => {
    const dateString = format(date, "yyyy-MM-dd");
    let newSelectedDates = [...selectedDates];

    if (newSelectedDates.includes(dateString)) {
      newSelectedDates = newSelectedDates.filter((d) => d !== dateString);
    } else {
      newSelectedDates.push(dateString);
    }

    setSelectedDates(newSelectedDates);

    onRangeChange(index, {
      startDate: newSelectedDates.length > 0 ? newSelectedDates[0] : "",
      endDate:
        newSelectedDates.length > 0
          ? newSelectedDates[newSelectedDates.length - 1]
          : "",
      selectedDates: newSelectedDates,
    });

    setQuickDate("Custom");
  };

  const removeIndividualDate = (dateToRemove) => {
    const newSelectedDates = selectedDates.filter(
      (date) => date !== dateToRemove
    );
    setSelectedDates(newSelectedDates);

    onRangeChange(index, {
      startDate: newSelectedDates.length > 0 ? newSelectedDates[0] : "",
      endDate:
        newSelectedDates.length > 0
          ? newSelectedDates[newSelectedDates.length - 1]
          : "",
      selectedDates: newSelectedDates,
    });

    if (newSelectedDates.length === 0) {
      setQuickDate("All Time");
    }
  };

  const datesToShow = future ? futureDates : normalDates;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Select
          value={quickDate}
          onChange={(e) => handleQuickDateChange(e.target.value)}
          sx={{ minWidth: 140, height: 40 }}
          size="small"
          disabled={individualDatesMode}
        >
          <MenuItem value="All Time" sx={{ height: 40 }}>
            {allTime.label}
          </MenuItem>
          {datesToShow.map(({ value, label }) => (
            <MenuItem key={value} value={value} sx={{ height: 40 }}>
              {label}
            </MenuItem>
          ))}
          <MenuItem value="Custom" sx={{ height: 40 }}>
            {custom.label}
          </MenuItem>
        </Select>

        {individualDatesMode ? (
          <DatePicker
            selected={null}
            onChange={handleIndividualDateChange}
            onInputClick={() => setIsCalendarOpen(true)}
            open={isCalendarOpen}
            onClickOutside={() => setIsCalendarOpen(false)}
            onCalendarClose={() => setIsCalendarOpen(false)}
            placeholderText="Select individual dates"
            filterDate={(date) => !isDateDisabled(date)}
            highlightDates={selectedDates.map((d) => parseISO(d))}
            inline={false}
            shouldCloseOnSelect={false}
            sx={{ width: 250, "& .MuiInputBase-root": { height: 40 } }}
          />
        ) : (
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            onInputClick={() => setIsCalendarOpen(true)}
            open={isCalendarOpen}
            onClickOutside={() => setIsCalendarOpen(false)}
            onCalendarClose={() => setIsCalendarOpen(false)}
            isClearable
            placeholderText="Select date range"
            filterDate={(date) => !isDateDisabled(date)}
            sx={{ width: 250, "& .MuiInputBase-root": { height: 40 } }}
          />
        )}
      </Box>

      {individualDatesMode && selectedDates.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {selectedDates.map((date) => (
            <Chip
              key={date}
              label={format(parseISO(date), "MMM dd, yyyy")}
              onDelete={() => removeIndividualDate(date)}
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const MultiDateRangePicker = ({ setRanges, reset, future = false }) => {
  const [dateRanges, setDateRanges] = useState([]);
  const [individualDatesMode, setIndividualDatesMode] = useState(false);

  useEffect(() => {
    if (reset) {
      setDateRanges([]);
      setIndividualDatesMode(false);
    }
  }, [reset]);

  const handleRangeChange = (index, range) => {
    const newRanges = [...dateRanges];
    newRanges[index] = range;
    setDateRanges(newRanges);
    updateParentRanges(newRanges);
  };

  const getDisabledDates = (currentIndex) => {
    return dateRanges
      .filter((_, index) => index !== currentIndex)
      .filter((range) => range.startDate && range.endDate);
  };

  const updateParentRanges = (ranges) => {
    if (setRanges) {
      const formattedRanges = ranges
        .filter(
          (range) =>
            (range.startDate && range.endDate) || range.selectedDates.length > 0
        )
        .map((range) => {
          if (range.selectedDates.length > 0) {
            return range.selectedDates.map((date) => ({
              startDate: date + "T00:00:00.000Z",
              endDate: date + "T23:59:59.999Z",
            }));
          } else {
            return {
              startDate: range.startDate + "T00:00:00.000Z",
              endDate: range.endDate + "T23:59:59.999Z",
            };
          }
        })
        .flat();

      setRanges(formattedRanges);
    }
  };

  const handleIndividualDatesToggle = (event) => {
    const isChecked = event.target.checked;
    setIndividualDatesMode(isChecked);

    if (isChecked && dateRanges.length > 1) {
      const firstRange = dateRanges[0];
      setDateRanges([firstRange]);
      updateParentRanges([firstRange]);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {dateRanges.length === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <QuickDateRangePicker
              range={{ startDate: "", endDate: "", selectedDates: [] }}
              index={0}
              onRangeChange={handleRangeChange}
              onRemove={() => {}}
              showRemove={false}
              future={future}
              individualDatesMode={individualDatesMode}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={individualDatesMode}
                onChange={handleIndividualDatesToggle}
              />
            }
            label="Select Individual Dates"
          />
        </Box>
      )}

      {dateRanges.map((range, index) => (
        <Box
          key={index}
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <QuickDateRangePicker
              range={range}
              index={index}
              onRangeChange={handleRangeChange}
              showRemove={dateRanges.length > 1 || index > 0}
              future={future}
              disabledDates={getDisabledDates(index)}
              individualDatesMode={individualDatesMode}
            />
          </Box>

          {index === 0 && dateRanges.length === 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={individualDatesMode}
                  onChange={handleIndividualDatesToggle}
                />
              }
              label="Select Individual Dates"
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default MultiDateRangePicker;
